import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core';
import { AsyncPipe, CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { VariableBenefitConfigurationSubmissionData } from '../../../../../../models/submissions/variable-benefit-configuration-submission-data';
import { LockedUnlockedBalance } from '../../../../../../models/shared/locked-unlocked-balance';
import { GoverningJurisdictionTypeId } from '../../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { PortalService } from '../../../../../../services/portal/portal.service';
import { map } from 'rxjs/operators';
import { VbAssetsUsedCard, vbAssetsUsedSummary } from '../../utils/vb-assets-used-cards.utils';
import { FinalLocationLineComponent } from '../../../../../../features/governing-jurisdiction-override/components/final-location-line/final-location-line.component';
import { FinalLocationJurisdictionTitleComponent } from '../../../../../../features/governing-jurisdiction-override/components/final-location-jurisdiction-title/final-location-jurisdiction-title.component';

@Component({
  selector: 'app-vb-config-assets-used-review-card',
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, CurrencyPipe, FinalLocationLineComponent, FinalLocationJurisdictionTitleComponent],
  templateUrl: './vb-config-assets-used-review-card.component.html',
  styleUrls: ['./vb-config-assets-used-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbConfigAssetsUsedReviewCardComponent implements OnChanges {
  @Input({ required: true }) submission!: VariableBenefitConfigurationSubmissionData;
  @Input() lockedUnlockedBalances: LockedUnlockedBalance[] | null = null;

  private portalService = inject(PortalService);

  public cards: VbAssetsUsedCard[] = [];
  public finalLocationId: GoverningJurisdictionTypeId | null = null;
  public total = 0;
  public showEntireBalanceDisclaimer = false;
  public showUnlockedFundsDisclaimer = false;
  public showDisclaimers = false;
  public unlockedFundsMarker = '*';
  public isZeroAssetsUsed = false;

  public unlockedFundsDisclaimer$ = this.portalService.isMember$.pipe(
    map(isMember => {
      if (isMember) {
        return $localize`Funds that were unlocked but not used during asset selection will remain as unlocked funds in the Defined Contribution Account(s).`;
      } else {
        return $localize`Funds that were unlocked but not used during asset selection will remain as unlocked funds in the member's Defined Contribution Account(s).`;
      }
    })
  );

  public ngOnChanges(): void {
    const summary = vbAssetsUsedSummary(this.submission?.sourceJurisdictions ?? [], this.lockedUnlockedBalances);

    this.cards = summary.cards;
    this.finalLocationId = this.submission?.governingJurisdictionOverrideId ?? null;
    this.total = summary.total;
    this.isZeroAssetsUsed = summary.total === 0;
    this.showEntireBalanceDisclaimer = summary.showEntireBalanceDisclaimer;
    this.showUnlockedFundsDisclaimer = summary.showUnlockedFundsDisclaimer;
    this.unlockedFundsMarker = summary.unlockedFundsMarker;
    this.showDisclaimers = summary.showEntireBalanceDisclaimer || summary.showUnlockedFundsDisclaimer;
  }

  public trackByJurisdictionId(_: number, card: VbAssetsUsedCard): GoverningJurisdictionTypeId {
    return card.jurisdictionId;
  }
}
